import { useQueries } from '@tanstack/react-query';
import { advisorAdminApi } from '@/features/wizard-advisor-admin';

export type AdvisorLookup = {
  advisorId: string;
  advisor: Advisor | null;
  isPending: boolean;
};

/**
 * Fires one parallel query per advisor ID. Missing advisors (e.g. IDs from a
 * developer's local environment that don't exist in the current DB) resolve to
 * `advisor: null` with no error surfaced to the UI.
 */
export function useAdvisorBatchLookup(advisorIds: readonly string[]): {
  lookups: AdvisorLookup[];
  isPending: boolean;
} {
  const queries = useQueries({
    queries: advisorIds.map(id => ({
      queryKey: ['wizardAdvisorLookup', 'byId', id] as const,
      queryFn: () => advisorAdminApi.searchAdvisor({ advisorId: id }),
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });

  const lookups: AdvisorLookup[] = advisorIds.map((advisorId, i) => ({
    advisorId,
    advisor: queries[i].data?.advisor ?? null,
    isPending: queries[i].isPending,
  }));

  return {
    lookups,
    isPending: queries.some(q => q.isPending),
  };
}
